/**
 * TenantContext — white-label subdomain detection + branding
 *
 * Reads the subdomain from window.location.hostname:
 *   alphaedge.marginmetrics.io   slug = "alphaedge"
 *   marginmetrics.io / app.marginmetrics.io / localhost  null (default platform)
 *
 * Fetches the matching tenant row from Supabase (if slug exists) and exposes
 * branding tokens (primaryColor, logoUrl, name) to the whole tree.
 */
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

//  Types 

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  logo_url: string | null;
  primary_color: string;
  plan: "standard" | "pro" | "enterprise";
  max_users: number;
  active: boolean;
}

interface TenantContextValue {
  tenant: Tenant | null;
  slug: string | null;
  loading: boolean;
  /** Whether the current session is running under a white-label subdomain */
  isWhiteLabel: boolean;
  /** Resolved accent colour (falls back to platform default) */
  primaryColor: string;
}

const DEFAULT_COLOR = "#1d9e75";

//  Helpers 

function detectSlug(): string | null {
  if (typeof window === "undefined") return null;
  const hostname = window.location.hostname; // e.g. "alphaedge.marginmetrics.io"
  const parts = hostname.split(".");
  // Treat as subdomain if: 3+ parts AND first part isn't "www", "app", "localhost"
  const IGNORED = new Set(["www", "app", "localhost", "staging", "preview"]);
  if (parts.length >= 3 && !IGNORED.has(parts[0])) {
    return parts[0];
  }
  return null;
}

//  Context 

const TenantContext = createContext<TenantContextValue>({
  tenant: null,
  slug: null,
  loading: false,
  isWhiteLabel: false,
  primaryColor: DEFAULT_COLOR,
});

export function TenantProvider({ children }: { children: ReactNode }) {
  const slug = detectSlug();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(!!slug);

  useEffect(() => {
    if (!slug) return;

    let cancelled = false;
    setLoading(true);

    supabase
      .from("tenants")
      .select("id, slug, name, logo_url, primary_color, plan, max_users, active")
      .eq("slug", slug)
      .eq("active", true)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data) {
          setTenant(data as Tenant);
          // Apply brand colour as CSS var so Tailwind utilities pick it up
          document.documentElement.style.setProperty(
            "--brand-primary",
            data.primary_color ?? DEFAULT_COLOR
          );
        }
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug]);

  const primaryColor = tenant?.primary_color ?? DEFAULT_COLOR;
  const isWhiteLabel = !!slug;

  return (
    <TenantContext.Provider value={{ tenant, slug, loading, isWhiteLabel, primaryColor }}>
      {children}
    </TenantContext.Provider>
  );
}

//  Hook 

export function useTenant() {
  return useContext(TenantContext);
}
